import { ApolloError } from "@apollo/client/core";

// The versioning tables ship ahead of some environments (rollout order: Hasura → API → UI, see
// ../ROLLOUT.md). Until the migration is applied there, queries against projectVersion /
// projectRevision fail with "not found in type" — the same expected drift AgentUsageHistory
// handles. Callers pass context { tolerateMissingSchema: true } so the Apollo error link stays
// quiet, and flip an `unavailable` state instead of erroring.

const errorMessages = (caughtError: unknown): string[] => {
  const messages: string[] = [];
  if (caughtError instanceof ApolloError) {
    caughtError.graphQLErrors.forEach((graphQLError) => messages.push(graphQLError.message));
    if (caughtError.message) messages.push(caughtError.message);
  } else if (caughtError instanceof Error) {
    messages.push(caughtError.message);
  }
  return messages;
};

/**
 * The same rollout drift one step finer: the projectVersion table EXISTS here, but its
 * entity_type check constraint predates the subject being logged, so Postgres rejects the row.
 * That is a constraint violation rather than a missing field, so isVersioningSchemaMissing cannot
 * see it — and without this the trail row would take the user's actual change down with it.
 *
 * Currently the gap is `special_condition`, added by Hasura migration 1787000000000 (RTLCA-5259).
 * Matching on the constraint name keeps this from swallowing unrelated check violations.
 */
export const isVersionEntityTypeUnsupported = (caughtError: unknown): boolean =>
  errorMessages(caughtError).some((message) => message.includes("project_version_entity_type_check"));

export const isVersioningSchemaMissing = (caughtError: unknown): boolean => {
  const messages = errorMessages(caughtError);
  return messages.some(
    (message) =>
      message.includes("not found in type: 'query_root'") ||
      message.includes("not found in type: 'mutation_root'") ||
      message.includes("not found in type: 'subscription_root'") ||
      // frozenAt, deletedAt and informational are here for the same reason as the rest: an
      // environment whose Hasura predates the column rejects the query — or the rename/delete
      // mutation's _set — on that field alone, and the page must degrade to "not deployed here yet"
      // rather than erroring. (informational is the column that marks a trail row a log line rather
      // than a version.)
      //
      // The source* names are the lineage columns (RTLCA-5470), read in the same document as the
      // trail: an environment behind that migration would otherwise turn the whole history page
      // into an error over a decoration on it.
      /field ['"]?(projectVersion\w*|projectRevision|activeRevision(Id)?|frozenAt|deletedAt|informational|revisionId|revision|sourceProject(Id)?|sourceVersionId|sourceVersionNumber)['"]? not found/i.test(
        message
      )
  );
};
